import React, { useMemo, useRef, useState, useEffect, useLayoutEffect } from 'react';
import { Box, Typography, useTheme, Chip, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import {
  startOfMonth,
  endOfMonth,
  addMonths,
  eachDayOfInterval,
  format,
  isSameDay,
  isSameMonth,
  isSameYear,
  isWithinInterval,
  isFuture,
  getDay,
  startOfDay,
  endOfDay
} from 'date-fns';

// All calendar dimensions in one place. Widths are in px because cell width
// is computed from measured text (see `measureWidest`), so the same numbers
// have to drive both the maths and the styles.
//
// Every narrow/wide pair is picked from the row's OWN measured width (see
// LAYOUT), never a viewport breakpoint: the Reports pane is a resizable
// fraction of the screen, so the viewport says nothing about the room these
// cards actually have.
const SIZES = {
  headerHeight: 40,
  cellHeight: { narrow: 32, wide: 34 },
  gridGapPx: 4,
  rowPadPx: 8,                                    // horizontal padding of the scrolling row
  rowGap: { narrow: 1, wide: 1.5 },               // gap between month cards
  cardPadPx: { narrow: 8, wide: 10 },
  cardBorderPx: 1,
  cellPadXPx: 3,                                  // breathing room each side of a value
  cellBorderPx: 1,                                // every cell draws a border, so it eats text room
  cellSlackPx: 1,                                 // covers sub-pixel rounding between measure and paint
  minCellPx: 36,                                  // keeps small values tappable
  peekPx: 24,                                     // width left for the next month's edge
  // PNL is the figure users actually look for, so it reads bigger than the
  // date label — otherwise same-size same-weight text reads as one visual
  // block instead of a value with a date caption.
  dateFont: { narrow: '0.625rem', wide: '0.6875rem' },  // 10px / 11px
  pnlFontPx: { narrow: 11, wide: 11.2 },
  pnlFontMinPx: 9,                                // floor before falling back to compact notation
  pnlWeight: 700,
  dayLabelFont: '0.65rem',
  titleFont: '0.8125rem',
};

// Card tiers, measured against the scrolling row's content width. Below
// `contentSizedMinPx` the cards fill the row and snap one month per swipe;
// at or above it each card is sized to its content. Below `peekMinPx` every
// pixel goes to the cells instead of the next month's peeking edge.
//
// Measured in JS rather than with CSS container queries because the width is
// already needed for cell sizing, so this adds no browser requirement —
// container queries would exclude iOS Safari 15, still in this project's
// browserslist (see headerFallbackSx).
const LAYOUT = {
  contentSizedMinPx: 568,
  peekMinPx: 328,
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Indian-unit amount capped at 5 characters so it fits the narrowest cell:
// -9.99, -39.0, +116, +3.9K, -12K, +1.3L, +2.1Cr. Exact value is in the tooltip.
const COMPACT_UNITS = [[1e7, 'Cr'], [1e5, 'L'], [1e3, 'K']];
const trimNumber = (v) => (v < 10 ? v.toFixed(1) : Math.round(v).toString()).replace(/\.0$/, '');
const formatCompactPnl = (value) => {
  const abs = Math.abs(value);
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  const unit = COMPACT_UNITS.find(([size]) => abs >= size);
  if (unit) return `${sign}${trimNumber(abs / unit[0])}${unit[1]}`;
  if (abs < 10) return `${sign}${parseFloat(abs.toFixed(2))}`;
  if (abs < 100) return `${sign}${parseFloat(abs.toFixed(1))}`;
  return `${sign}${Math.round(abs)}`;
};

// Default in-cell format: exact, always 2 decimals (-14.20, +99999999.99).
const formatPnl = (value) => {
  const abs = Math.abs(value);
  return `${value > 0 ? '+' : value < 0 ? '-' : ''}${abs.toFixed(2)}`;
};

// Last resort for the narrowest cells: whole units only (+9Cr, -1L, +116).
// Nothing narrower exists, so this is what guarantees a value always fits.
const formatTersePnl = (value) => {
  const abs = Math.abs(value);
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  const unit = COMPACT_UNITS.find(([size]) => abs >= size);
  return `${sign}${Math.round(unit ? abs / unit[0] : abs)}${unit ? unit[1] : ''}`;
};

// Canvas gives exact, font-aware text widths without touching layout. The
// font shorthand needs multi-word families quoted or it silently fails to
// parse and `ctx.font` keeps its previous value.
const toCanvasFamily = (family) =>
  String(family)
    .split(',')
    .map((part) => {
      const name = part.trim().replace(/^["']|["']$/g, '');
      return /\s/.test(name) ? `"${name}"` : name;
    })
    .join(', ');

let measureCtx;
/** Widest of `texts` in px at the given font, 0 for an empty list. */
const measureWidest = (texts, fontPx, family) => {
  if (!texts.length) return 0;

  if (measureCtx === undefined) {
    measureCtx = document.createElement('canvas').getContext('2d') || null;
  }
  if (measureCtx) {
    measureCtx.font = `${SIZES.pnlWeight} ${fontPx}px ${toCanvasFamily(family)}`;
    if (measureCtx.font.includes(`${fontPx}px`)) {
      return texts.reduce((max, text) => Math.max(max, measureCtx.measureText(text).width), 0);
    }
  }
  // Canvas unavailable or the font failed to parse: 0.62em per character is a
  // measured upper bound for this font, so cells end up wider, never clipped.
  return texts.reduce((max, text) => Math.max(max, text.length * fontPx * 0.62), 0);
};

const formatExactPnl = (value) =>
  `${value < 0 ? '-' : value > 0 ? '+' : ''}₹${Math.abs(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Full and short range labels; the header picks one based on its own width.
// Exported so the filter bar's period chip formats a range exactly the same.
export const formatRange = (start, end) => {
  const sameYear = isSameYear(start, end);
  const full = sameYear
    ? `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`
    : `${format(start, 'd MMM yyyy')} – ${format(end, 'd MMM yyyy')}`;
  const short = sameYear && isSameMonth(start, end)
    ? `${format(start, 'd')}–${format(end, 'd MMM')}`
    : sameYear
      ? `${format(start, 'd MMM')} – ${format(end, 'd MMM')}`
      : `${format(start, 'MMM yy')} – ${format(end, 'MMM yy')}`;
  return { full, short };
};

// The header responds to its OWN width (CSS container queries), not the
// viewport: the Reports pane is a resizable fraction of the screen, so
// viewport breakpoints don't reflect how much room the header really has.
// Each tier drops, shortens or tightens one thing; the P&L amount is never cut.
// Rules target descendants only — an element cannot query itself, which is why
// the container sits on a wrapper around the row.
const HEADER_TIERS = [
  [820, { '& .cal-title': { display: 'none' } }],
  [680, {
    '& .cal-day-full, & .cal-sum-full': { display: 'none' },
    '& .cal-day-short, & .cal-sum-short': { display: 'inline' },
  }],
  [600, {
    // Tighten spacing before anything has to be dropped.
    '& .cal-row': { gap: 0.75, px: 1 },
    '& .cal-icon': { fontSize: 18 },
    '& .MuiChip-root': { fontSize: '0.65rem' },
    '& .MuiChip-label': { px: 0.75 },
  }],
  [520, {
    '& .cal-range-full, & .cal-sum-short': { display: 'none' },
    '& .cal-range-short': { display: 'inline' },
  }],
  // Phones: the selected-day chip replaces the range chip
  [400, { '&[data-has-day="true"] .cal-range-chip': { display: 'none' } }],
];
const headerTierSx = Object.fromEntries(
  HEADER_TIERS.map(([width, rules]) => [`@container (max-width: ${width - 0.02}px)`, rules])
);

// Fallback for browsers without container-query support (e.g. iOS Safari
// <16, still >0.2% of this project's browserslist target). Every fixed-width
// header child except the amount is flexShrink: 0, so without the tiers
// above narrowing the header can silently clip content instead of hiding it
// gracefully. Force the narrowest tier's DOM state unconditionally — the
// same one already verified overflow-free at 320px — so degraded browsers
// lose the nice per-width shortening but never clip.
const headerFallbackSx = {
  '@supports not (container-type: inline-size)': {
    ...HEADER_TIERS.reduce((merged, [, rules]) => {
      for (const [selector, decl] of Object.entries(rules)) merged[selector] = { ...merged[selector], ...decl };
      return merged;
    }, {}),
  },
};

/**
 * Pinned header row: range, selected day, an optional summary (label +
 * value) and the show/hide arrow. Chips shrink with an ellipsis as a last
 * resort, so the row never overflows at any width.
 *
 * @param {{ label: string, shortLabel?: string, value: ReactNode }} [summary]
 */
export const CalendarHeader = ({
  startDate,
  endDate,
  selectedDay = null,
  onClearSelection,
  isExpanded,
  onToggle,
  summary = null,
}) => {
  const theme = useTheme();
  if (!startDate || !endDate) return null;

  const range = formatRange(startDate, endDate);

  const handleClearSelection = (e) => {
    e.stopPropagation(); // don't also toggle the calendar
    onClearSelection?.();
  };

  const shrinkableChipSx = {
    height: 24,
    minWidth: 0,
    maxWidth: '100%',
    fontSize: '0.7rem',
    '& .MuiChip-label': { px: 1.25, overflow: 'hidden', textOverflow: 'ellipsis' },
  };

  return (
    // Wrapper carries the container so the tiers above can size the row
    // itself, not just its children.
    <Box
      data-has-day={selectedDay ? 'true' : 'false'}
      sx={{
        containerType: 'inline-size',
        minWidth: 0,
        '& .cal-range-short, & .cal-day-short, & .cal-sum-short': { display: 'none' },
        ...headerTierSx,
        ...headerFallbackSx,
      }}
    >
      <Box
        id="calendar-header-toggle"
        className="cal-row"
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2,
          height: SIZES.headerHeight,
          minWidth: 0,
          overflow: 'hidden',
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          cursor: 'pointer',
          userSelect: 'none',
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderRadius: '8px 8px 0 0',
        }}
      >
        <CalendarMonthIcon className="cal-icon" sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
        <Typography
          className="cal-title"
          sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.8rem', flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          Calendar View
        </Typography>

        {/* Chips share the flexible middle and truncate before anything else */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: '0 1 auto', overflow: 'hidden' }}>
          <Chip
            className="cal-range-chip"
            label={<><span className="cal-range-full">{range.full}</span><span className="cal-range-short">{range.short}</span></>}
            size="small"
            variant="outlined"
            sx={{
              ...shrinkableChipSx,
              flexShrink: 3, // truncates before the selected-day chip
              fontWeight: 500,
              borderColor: theme.palette.divider,
              color: 'text.secondary',
            }}
          />
          {selectedDay && (
            <Chip
              label={<>
                <span className="cal-day-full">{`Showing: ${format(selectedDay, 'dd MMM yyyy')}`}</span>
                <span className="cal-day-short">{format(selectedDay, 'd MMM')}</span>
              </>}
              onDelete={handleClearSelection}
              onClick={handleClearSelection}
              deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                ...shrinkableChipSx,
                flexShrink: 1,
                fontWeight: 600,
                '& .MuiChip-deleteIcon': { ml: 0.25, color: theme.palette.primary.main },
              }}
            />
          )}
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }} />

        {summary && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, whiteSpace: 'nowrap' }}>
            <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
              <span className="cal-sum-full">{summary.label}</span>
              <span className="cal-sum-short">{summary.shortLabel || summary.label}</span>
            </Typography>
            {summary.value}
          </Box>
        )}

        {isExpanded
          ? <KeyboardArrowUpIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
          : <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />}
      </Box>
    </Box>
  );
};

/**
 * One card per month that overlaps [startDate, endDate]. Each card shows
 * only the weeks that contain at least one in-range day.
 */
export const CalendarMonthCards = ({
  startDate,
  endDate,
  data = [],
  selectedDay = null,
  onDayClick = () => {},
}) => {
  const theme = useTheme();

  // Lookup of 'yyyy-MM-dd' → PnL. A day absent from `data` had no trades.
  const pnlByDate = useMemo(() => {
    const map = {};
    data.forEach((item) => {
      map[item.date] = Number(item.pnl) || 0;
    });
    return map;
  }, [data]);

  const monthBlocks = useMemo(() => {
    if (!startDate || !endDate) return [];
    const range = { start: startOfDay(startDate), end: endOfDay(endDate) };
    const months = [];

    for (let month = startOfMonth(range.start); month <= range.end; month = addMonths(month, 1)) {
      const days = eachDayOfInterval({ start: month, end: endOfMonth(month) });

      // Sunday-first grid, padded to whole weeks
      const cells = [...Array(getDay(days[0])).fill(null), ...days];
      while (cells.length % 7 !== 0) cells.push(null);

      const allWeeks = [];
      for (let i = 0; i < cells.length; i += 7) allWeeks.push(cells.slice(i, i + 7));
      const inRangeWeeks = allWeeks.filter((week) => week.some((day) => day && isWithinInterval(day, range)));

      if (inRangeWeeks.length) {
        months.push({ id: format(month, 'yyyy-MM'), title: format(month, 'MMMM yyyy'), allWeeks, inRangeWeeks });
      }
    }

    // Trimming to just the in-range weeks only shortens the calendar when a
    // single card is shown. With several cards side by side, the tallest one
    // still sets the row's height, so trimming the others buys nothing and
    // just leaves the cards uneven — show every card's full month instead.
    const showFullMonth = months.length > 1;
    return months.map((m) => ({
      id: m.id,
      title: m.title,
      // Resolve each cell once so sizing and rendering can never disagree
      // about which values are actually on screen.
      cells: (showFullMonth ? m.allWeeks : m.inRangeWeeks).flat().map((day) => {
        if (!day) return null;
        const dateStr = format(day, 'yyyy-MM-dd');
        const hasData = dateStr in pnlByDate;
        return {
          day,
          dateStr,
          hasData,
          pnl: hasData ? pnlByDate[dateStr] : 0,
          isInactive: !isWithinInterval(day, range) || isFuture(day),
        };
      }),
    }));
  }, [startDate, endDate, pnlByDate]);

  // Values that will actually render — the only ones cell width must fit.
  const shownValues = useMemo(() => {
    const values = [];
    monthBlocks.forEach((block) => block.cells.forEach((cell) => {
      if (cell && cell.hasData && !cell.isInactive) values.push(cell.pnl);
    }));
    return values;
  }, [monthBlocks]);

  // Content width of the scrolling row (padding already excluded, matching
  // ResizeObserver's contentRect), measured before paint so the first frame
  // is already sized, then re-measured whenever the pane is resized.
  const rowRef = useRef(null);
  const [rowContentWidth, setRowContentWidth] = useState(0);
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    const style = getComputedStyle(row);
    setRowContentWidth(row.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight));
    const observer = new ResizeObserver(([entry]) => setRowContentWidth(entry.contentRect.width));
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  // Everything responsive keys off the row's measured width, so a narrow pane
  // on a wide monitor is treated as narrow. Before the first measurement the
  // row is treated as narrow; the layout effect above settles it before paint.
  const isWide = rowContentWidth >= LAYOUT.contentSizedMinPx;
  const allowPeek = rowContentWidth >= LAYOUT.peekMinPx;
  const tier = isWide ? 'wide' : 'narrow';

  // Webfont metrics differ from the fallback font, so re-measure once loaded.
  const [fontsReady, setFontsReady] = useState(() => !document.fonts);
  useEffect(() => {
    if (fontsReady) return undefined;
    let cancelled = false;
    document.fonts.ready.then(() => { if (!cancelled) setFontsReady(true); });
    return () => { cancelled = true; };
  }, [fontsReady]);

  // Start on the latest month (the newest data) whenever the range changes.
  useLayoutEffect(() => {
    if (rowRef.current) rowRef.current.scrollLeft = rowRef.current.scrollWidth;
  }, [monthBlocks]);

  /**
   * Cell width comes from the widest value actually on screen, so every cell
   * is identical and nothing is ever clipped. When the widest value can't fit
   * the space available, shrink the font (text width scales linearly, so this
   * is one step, not a search) and only fall back to compact notation once
   * even the floor font size can't fit — which in practice is narrow panes only.
   */
  const { cellPx, cardPx, fontPx, formatValue } = useMemo(() => {
    const hasMultipleMonths = monthBlocks.length > 1;
    const basePx = SIZES.pnlFontPx[tier];
    const family = theme.typography.fontFamily;
    const cardChromePx = 2 * SIZES.cardPadPx[tier]
      + 2 * SIZES.cardBorderPx
      + 6 * SIZES.gridGapPx;

    // Widest a card may be before the row starts scrolling.
    const cardLimit = !isWide && hasMultipleMonths && allowPeek
      ? rowContentWidth - SIZES.peekPx
      : rowContentWidth;
    const cellLimit = rowContentWidth > 0 ? (cardLimit - cardChromePx) / 7 : Infinity;

    // Room a value gets inside a cell, once padding and borders are removed.
    const cellChromePx = 2 * SIZES.cellPadXPx + 2 * SIZES.cellBorderPx;
    const widestFull = measureWidest(shownValues.map(formatPnl), basePx, family);
    const wantedCell = Math.ceil(widestFull + cellChromePx + 2 * SIZES.cellSlackPx);

    // Narrow rows keep full-width cards (one month per swipe), so their cells
    // are whatever the row allows; wider rows grow the card to the content.
    const rawCell = isWide ? Math.max(SIZES.minCellPx, Math.min(wantedCell, cellLimit)) : cellLimit;
    const cellPx = Number.isFinite(rawCell) ? Math.floor(rawCell) : Math.ceil(wantedCell);
    const textPx = cellPx - cellChromePx;

    // Fit to slightly less than the real space so sub-pixel rounding between
    // measuring and painting can never clip the last glyph.
    const room = textPx - SIZES.cellSlackPx;
    const fit = (texts) => {
      const widest = measureWidest(texts, basePx, family);
      if (widest <= room || widest === 0) return { fontPx: basePx, fits: true };
      const scaled = basePx * (room / widest);
      return scaled >= SIZES.pnlFontMinPx
        ? { fontPx: scaled, fits: true }
        : { fontPx: SIZES.pnlFontMinPx, fits: false };
    };

    // Exact first; shorter notations only when the space genuinely can't take
    // it. The last candidate is the narrowest possible, so something always
    // fits and the tooltip still carries the exact value.
    let chosen;
    for (const formatValue of [formatPnl, formatCompactPnl, formatTersePnl]) {
      const result = { ...fit(shownValues.map(formatValue)), formatValue };
      chosen = result;
      if (result.fits) break;
    }

    return {
      cellPx,
      cardPx: 7 * cellPx + cardChromePx,
      fontPx: Math.round(chosen.fontPx * 100) / 100,
      formatValue: chosen.formatValue,
    };
    // `fontsReady` is not read directly — it re-runs the measurement once the
    // webfont replaces the fallback font.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthBlocks, shownValues, rowContentWidth, isWide, allowPeek, tier, theme.typography.fontFamily, fontsReady]);

  const isDark = theme.palette.mode === 'dark';
  const activeBg = isDark ? 'rgba(144, 202, 249, 0.16)' : '#e3f2fd';
  const hoverBg = isDark ? 'rgba(144, 202, 249, 0.24)' : '#d0e9fc';
  const inactiveBorder = isDark ? 'rgba(144, 202, 249, 0.16)' : '#e3f2fd';
  const cellHeight = SIZES.cellHeight[tier];

  const grid = {
    display: 'grid',
    // Fixed px on wider rows keeps every card's columns identical; narrow rows
    // use fractions so a full-width card can never overflow.
    gridTemplateColumns: isWide ? `repeat(7, ${cellPx}px)` : 'repeat(7, minmax(0, 1fr))',
    columnGap: `${SIZES.gridGapPx}px`,
  };

  return (
    <Box
      ref={rowRef}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: SIZES.rowGap[tier],
        px: `${SIZES.rowPadPx}px`,
        py: 1,
        overflowX: 'auto',
        // Narrow rows: one month per swipe
        scrollSnapType: isWide ? 'none' : 'x mandatory',
        // index.css hides every scrollbar (`* { scrollbar-width: none }` plus
        // `::-webkit-scrollbar { display: none }`), so this row opts back in.
        // It must be `auto`, not `thin`: any non-auto scrollbar-width makes
        // Chrome ignore the ::-webkit-scrollbar rules below and fall back to a
        // native overlay bar that fades on macOS. With `auto` the styled bar
        // is drawn and stays visible whenever the row overflows.
        scrollbarWidth: 'auto',
        '&::-webkit-scrollbar': { display: 'block', height: '6px' },
        '&::-webkit-scrollbar-track': { background: isDark ? 'rgba(255,255,255,0.05)' : '#f1f1f1', borderRadius: '8px' },
        '&::-webkit-scrollbar-thumb': {
          background: isDark ? 'rgba(255,255,255,0.2)' : '#c1c1c1',
          borderRadius: '8px',
          '&:hover': { background: isDark ? 'rgba(255,255,255,0.3)' : '#a8a8a8' },
        },
      }}
    >
      {monthBlocks.map((block) => (
        <Box
          key={block.id}
          sx={{
            flex: isWide
              ? `0 0 ${cardPx}px`
              : monthBlocks.length > 1 && allowPeek
                ? `0 0 calc(100% - ${SIZES.peekPx}px)`
                : '0 0 100%',
            minWidth: 0,
            boxSizing: 'border-box',
            scrollSnapAlign: 'start',
            border: `${SIZES.cardBorderPx}px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: `${SIZES.cardPadPx[tier]}px`,
            bgcolor: 'background.paper',
          }}
        >
          <Typography sx={{ fontSize: SIZES.titleFont, fontWeight: 700, color: 'text.primary', mb: 0.75, lineHeight: 1.2 }}>
            {block.title}
          </Typography>

          <Box sx={{ ...grid, mb: 0.5 }}>
            {DAY_LABELS.map((label) => (
              <Typography key={label} sx={{ fontSize: SIZES.dayLabelFont, color: 'text.secondary', fontWeight: 600, textAlign: 'center', lineHeight: 1.4 }}>
                {label}
              </Typography>
            ))}
          </Box>

          <Box sx={{ ...grid, rowGap: `${SIZES.gridGapPx}px` }}>
            {block.cells.map((cellData, idx) => {
              if (!cellData) return <Box key={`empty-${idx}`} sx={{ height: cellHeight }} />;

              const { day, dateStr, hasData, pnl, isInactive } = cellData;
              const isSelected = selectedDay && isSameDay(day, selectedDay);
              const showValue = hasData && !isInactive;
              const pnlColor = pnl > 0 ? theme.palette.success.main : pnl < 0 ? theme.palette.error.main : 'text.secondary';

              const cell = (
                <Box
                  onClick={!isInactive ? () => onDayClick(day) : undefined}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: cellHeight,
                    minWidth: 0,
                    px: `${SIZES.cellPadXPx}px`,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    borderRadius: 1.5,
                    cursor: isInactive ? 'default' : 'pointer',
                    bgcolor: isInactive ? 'transparent' : activeBg,
                    border: isSelected
                      ? `1px solid ${theme.palette.primary.main}`
                      : `1px solid ${isInactive ? inactiveBorder : 'transparent'}`,
                    opacity: isInactive ? 0.7 : 1,
                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                    '&:hover': !isInactive ? { bgcolor: hoverBg } : {},
                  }}
                >
                  <Typography sx={{ fontSize: SIZES.dateFont[tier], lineHeight: 1.2, color: isInactive ? 'text.disabled' : 'text.primary', fontWeight: isInactive ? 500 : 600 }}>
                    {format(day, 'd')}
                  </Typography>
                  <Typography
                    sx={{
                      // No letter-spacing or font-variant-numeric here: canvas
                      // measurement ignores both, so either would make the
                      // rendered text wider than the cell computed for it.
                      fontSize: `${fontPx}px`,
                      lineHeight: 1.2,
                      maxWidth: '100%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: showValue ? pnlColor : isInactive ? 'text.disabled' : 'text.secondary',
                      fontWeight: showValue ? SIZES.pnlWeight : 500,
                    }}
                  >
                    {showValue ? formatValue(pnl) : '-'}
                  </Typography>
                </Box>
              );

              return isInactive ? (
                <React.Fragment key={dateStr}>{cell}</React.Fragment>
              ) : (
                <Tooltip
                  key={dateStr}
                  title={`${hasData ? formatExactPnl(pnl) : 'No trades'} · ${format(day, 'd MMM yyyy')}`}
                  enterDelay={300}
                  disableInteractive
                  arrow
                >
                  {cell}
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
